// Selective Color — Photoshop-style adjustment.
//
// Adjusts CMYK ink amounts in nine color ranges: Reds, Yellows, Greens,
// Cyans, Blues, Magentas, Whites, Neutrals, and Blacks. Each range has
// four sliders (Cyan, Magenta, Yellow, Black) from -100% to +100%.
//
// Two methods:
//   Relative — changes are proportional to the existing ink amount.
//   Absolute — changes are added directly.
//
// Alpha is never touched. Selection masking is delegated to
// `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface SelectiveColorRange {
  cyan: number;
  magenta: number;
  yellow: number;
  black: number;
}

export interface SelectiveColorParams {
  reds: SelectiveColorRange;
  yellows: SelectiveColorRange;
  greens: SelectiveColorRange;
  cyans: SelectiveColorRange;
  blues: SelectiveColorRange;
  magentas: SelectiveColorRange;
  whites: SelectiveColorRange;
  neutrals: SelectiveColorRange;
  blacks: SelectiveColorRange;
  method: "relative" | "absolute";
}

const ZERO_RANGE: SelectiveColorRange = { cyan: 0, magenta: 0, yellow: 0, black: 0 };

const DEFAULT_PARAMS: SelectiveColorParams = {
  reds: { ...ZERO_RANGE },
  yellows: { ...ZERO_RANGE },
  greens: { ...ZERO_RANGE },
  cyans: { ...ZERO_RANGE },
  blues: { ...ZERO_RANGE },
  magentas: { ...ZERO_RANGE },
  whites: { ...ZERO_RANGE },
  neutrals: { ...ZERO_RANGE },
  blacks: { ...ZERO_RANGE },
  method: "relative",
};

interface ColorRangeWeights {
  reds: number;
  yellows: number;
  greens: number;
  cyans: number;
  blues: number;
  magentas: number;
  whites: number;
  neutrals: number;
  blacks: number;
}

function getColorRangeWeights(r: number, g: number, b: number): ColorRangeWeights {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const lum = (r * 0.3 + g * 0.59 + b * 0.11) / 255;

  return {
    reds: r > g && r > b ? (r - Math.max(g, b)) / 255 : 0,
    yellows: r > b && g > b ? Math.min(r, g) / 255 - b / 255 : 0,
    greens: g > r && g > b ? (g - Math.max(r, b)) / 255 : 0,
    cyans: g > r && b > r ? Math.min(g, b) / 255 - r / 255 : 0,
    blues: b > r && b > g ? (b - Math.max(r, g)) / 255 : 0,
    magentas: r > g && b > g ? Math.min(r, b) / 255 - g / 255 : 0,
    whites: lum > 0.5 ? (lum - 0.5) * 2 : 0,
    neutrals: lum > 0.25 && lum < 0.75 ? 1 - Math.abs(lum - 0.5) * 4 : 0,
    blacks: lum < 0.5 ? (0.5 - lum) * 2 : 0,
  };
}

function rgbToCmyk(r: number, g: number, b: number): [number, number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const k = 1 - Math.max(rn, gn, bn);

  if (k >= 1) return [0, 0, 0, 1];

  const denom = 1 - k;
  return [
    (1 - rn - k) / denom,
    (1 - gn - k) / denom,
    (1 - bn - k) / denom,
    k,
  ];
}

function cmykToRgb(c: number, m: number, y: number, k: number): [number, number, number] {
  const r = Math.round(255 * (1 - c) * (1 - k));
  const g = Math.round(255 * (1 - m) * (1 - k));
  const b = Math.round(255 * (1 - y) * (1 - k));
  return [r, g, b];
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const SelectiveColor: Filter<SelectiveColorParams> = {
  id: "selective-color",
  displayName: "Selective Color",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: SelectiveColorParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const ranges = {
      reds: {
        cyan: clampRange(params.reds.cyan, -100, 100),
        magenta: clampRange(params.reds.magenta, -100, 100),
        yellow: clampRange(params.reds.yellow, -100, 100),
        black: clampRange(params.reds.black, -100, 100),
      },
      yellows: {
        cyan: clampRange(params.yellows.cyan, -100, 100),
        magenta: clampRange(params.yellows.magenta, -100, 100),
        yellow: clampRange(params.yellows.yellow, -100, 100),
        black: clampRange(params.yellows.black, -100, 100),
      },
      greens: {
        cyan: clampRange(params.greens.cyan, -100, 100),
        magenta: clampRange(params.greens.magenta, -100, 100),
        yellow: clampRange(params.greens.yellow, -100, 100),
        black: clampRange(params.greens.black, -100, 100),
      },
      cyans: {
        cyan: clampRange(params.cyans.cyan, -100, 100),
        magenta: clampRange(params.cyans.magenta, -100, 100),
        yellow: clampRange(params.cyans.yellow, -100, 100),
        black: clampRange(params.cyans.black, -100, 100),
      },
      blues: {
        cyan: clampRange(params.blues.cyan, -100, 100),
        magenta: clampRange(params.blues.magenta, -100, 100),
        yellow: clampRange(params.blues.yellow, -100, 100),
        black: clampRange(params.blues.black, -100, 100),
      },
      magentas: {
        cyan: clampRange(params.magentas.cyan, -100, 100),
        magenta: clampRange(params.magentas.magenta, -100, 100),
        yellow: clampRange(params.magentas.yellow, -100, 100),
        black: clampRange(params.magentas.black, -100, 100),
      },
      whites: {
        cyan: clampRange(params.whites.cyan, -100, 100),
        magenta: clampRange(params.whites.magenta, -100, 100),
        yellow: clampRange(params.whites.yellow, -100, 100),
        black: clampRange(params.whites.black, -100, 100),
      },
      neutrals: {
        cyan: clampRange(params.neutrals.cyan, -100, 100),
        magenta: clampRange(params.neutrals.magenta, -100, 100),
        yellow: clampRange(params.neutrals.yellow, -100, 100),
        black: clampRange(params.neutrals.black, -100, 100),
      },
      blacks: {
        cyan: clampRange(params.blacks.cyan, -100, 100),
        magenta: clampRange(params.blacks.magenta, -100, 100),
        yellow: clampRange(params.blacks.yellow, -100, 100),
        black: clampRange(params.blacks.black, -100, 100),
      },
    };
    const method = params.method;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      let [c, m, y, k] = rgbToCmyk(r, g, b);

      const weights = getColorRangeWeights(r, g, b);

      const rangeEntries: [keyof ColorRangeWeights, typeof ranges.reds][] = [
        ["reds", ranges.reds],
        ["yellows", ranges.yellows],
        ["greens", ranges.greens],
        ["cyans", ranges.cyans],
        ["blues", ranges.blues],
        ["magentas", ranges.magentas],
        ["whites", ranges.whites],
        ["neutrals", ranges.neutrals],
        ["blacks", ranges.blacks],
      ];

      for (const [key, range] of rangeEntries) {
        const wgt = weights[key];
        if (wgt <= 0) continue;

        if (method === "relative") {
          c += (range.cyan / 100) * c * wgt;
          m += (range.magenta / 100) * m * wgt;
          y += (range.yellow / 100) * y * wgt;
          k += (range.black / 100) * k * wgt;
        } else {
          c += (range.cyan / 100) * wgt;
          m += (range.magenta / 100) * wgt;
          y += (range.yellow / 100) * wgt;
          k += (range.black / 100) * wgt;
        }
      }

      c = clamp01(c);
      m = clamp01(m);
      y = clamp01(y);
      k = clamp01(k);

      let [ro, go, bo] = cmykToRgb(c, m, y, k);
      ro = clampByte(ro);
      go = clampByte(go);
      bo = clampByte(bo);

      dst[o] = ro;
      dst[o + 1] = go;
      dst[o + 2] = bo;
      dst[o + 3] = a;
    }

    return out;
  },
};
